import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import {
  RegistrationCreateRequestBody,
  useRegistrationCreate,
} from "@gocardless/api/dashboard/registration";
import { getErrorResponseErrors } from "@gocardless/api/utils/error";
import { Trans } from "@lingui/macro";
import { HTTPStatusCode } from "@gocardless/api/http/http-status-codes";
import { TemporaryAccessTokensObject } from "@gocardless/api/dashboard/types";
import { useClearSWR } from "@gocardless/api/utils/use-clear-cache";
import { useLingui } from "@lingui/react";
import { useRouter } from "next/router";

import { SignUpFormData, SignUpFormField, SignUpAPIErrors } from "./consts";
import { SignUpAccountType } from "./SignUp";

import { useToastNotification } from "src/hooks/useToastNotification";
import { Route, getRouteURL } from "src/common/routing";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { setAccessToken } from "src/common/authorisation/session";
import { FieldLevelError, FieldLevelErrorMap } from "src/common/api";
import {
  GA4Events,
  sendGA4Event,
} from "src/technical-integrations/third-party-tracking/google-analytics";
import { CountryCodeSupported } from "src/common/country";
import { COUNTRY_CODES } from "src/common/constants/countryCodes";
import { getItem, removeItem } from "src/common/local-storage/local-storage";
import {
  isCountryCodeSupported,
  isSandboxEnvironment,
} from "src/common/consts";
import { TrackingEvent } from "src/common/trackingEvents";
import { CookieStorage } from "src/common/constants/storage";
import {
  REDDIT_TRACKING_EVENT,
  trackReddit,
} from "src/technical-integrations/third-party-tracking/Reddit";
import {
  ReferralEmailSource,
  ReferralEmailVariation,
} from "src/common/trackingEventAttributes";
import { isRole } from "src/common/config";
import { Role } from "src/common/environments";

interface RegistrationResponse {
  linked: {
    temporary_access_tokens: TemporaryAccessTokensObject;
  };
  registrations: {
    organisation_created_at: string;
    cpv_enabled?: boolean;
    account_type?: SignUpAccountType;
    links: {
      creditor: string;
      organisation: string;
      temporary_access_token: string;
    };
  };
}

export const OB_SEARCH_QUERY_KEY = "gc.ob.search.query";

export const useSignUp = (
  referralGeo: string | null,
  referralCode: string | null,
  referralSource?: ReferralEmailSource | null,
  referralVariation?: ReferralEmailVariation | null,
  referralCodeName?: string | null
) => {
  const { i18n } = useLingui();
  const clearCache = useClearSWR();
  const router = useRouter();

  const [apiFieldErrors, setAPIFieldErrors] = useState<
    FieldLevelErrorMap<SignUpFormField>
  >(new Map());
  const [registrationBody, setRegistrationBody] =
    useState<RegistrationCreateRequestBody>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { sendEvent, group, identify } = useSegment();

  const item = getItem(OB_SEARCH_QUERY_KEY);
  const referred_from = item ? { referred_from: "bank_account_data" } : null;

  const { triggerSuccessNotification, triggerErrorNotification } =
    useToastNotification();

  // referral_code is ignored if in sandbox or not in an allowed geo
  const hasReferralCode = !!(
    !isSandboxEnvironment &&
    referralCode &&
    referralGeo &&
    isCountryCodeSupported(referralGeo) &&
    referralGeo !== CountryCodeSupported.DE
  );

  const preSignupDeviceID = Cookies.get(CookieStorage.PreSignupOptimizelyID);
  const gaClientID = (Cookies.get(CookieStorage.GACookieClientID) || "")
    .split(".")
    .slice(-2)
    .join(".");
  const impactClickID = Cookies.get(CookieStorage.ImpactClickID);
  const awinClickID = Cookies.get(CookieStorage.AWinClickID);

  const [submitRegistration] = useRegistrationCreate({
    onSuccess: async (response) => {
      try {
        if (!response) {
          throw new Error("No response from API");
        }
        const res = response as RegistrationResponse;
        const userEmail = registrationBody.email;
        triggerSuccessNotification({
          title: (
            <Trans id="sign-up.success-notification.title">
              Thanks for signing up to GoCardless!
            </Trans>
          ),
          message: (
            <Trans id="sign-up.success-notification.text">
              We have sent you a verification email to {userEmail}.
            </Trans>
          ),
        });

        setAccessToken(res.linked, true);
        const userDetails = res.linked.temporary_access_tokens?.links;
        const organisationID = res.registrations.links.organisation;
        const accountType = res.registrations.account_type;
        const signUpDate = (
          res.registrations.organisation_created_at ?? new Date().toISOString()
        )?.substring(0, 10);
        const cpvEnabled = !!res.registrations.cpv_enabled;
        const countryName =
          COUNTRY_CODES(i18n).find(({ code }) => code === registrationBody.geo)
            ?.name || "";

        if (userDetails?.user) {
          await identify(userDetails?.user, {
            creditor_id: userDetails?.creditor,
            organisation_id: organisationID,
            pre_signup_device_id: preSignupDeviceID,
            ga_client_id: gaClientID,
            created_at: signUpDate,
          });
        }
        await group(organisationID, {
          created_at: signUpDate,
          signup_source: "website",
          organisation_id: organisationID,
          merchant_type: registrationBody.creditor_type,
          country_name: countryName,
          has_been_referred: hasReferralCode,
          ...referred_from,
        });
        if (referred_from) {
          removeItem(OB_SEARCH_QUERY_KEY);
        }
        sendEvent(TrackingEvent.SIGNUP_ACCOUNT_CREATED, {
          has_referral_code: hasReferralCode,
          partner_name: null,
          organisation_id: organisationID,
          user_id: userDetails?.user,
          context: {
            referrer: {
              type: "impactRadius",
              id: impactClickID,
            },
          },
          category: "Sign - up",
          revenue: 5,
          creditor_type: registrationBody.creditor_type,
          business_location: registrationBody.geo,
          awinclickid: awinClickID,
          referral_source: referralSource,
          referral_variation: referralVariation,
          referrer: referralCodeName,
        });
        sendGA4Event(GA4Events.SignUpSuccess, {
          organisationId: organisationID,
          anonymous_client_id: preSignupDeviceID,
        });
        trackReddit().trackRedditEvent(REDDIT_TRACKING_EVENT.SIGNUP);

        clearCache();
        router.push(
          getRouteURL({
            route: cpvEnabled
              ? Route.Intro
              : accountType === SignUpAccountType.SALES_SERVED ||
                  isRole(Role.Sandbox)
                ? Route.Home
                : Route.OnboardingWelcome,
            absolute: true,
          })
        ) as unknown as Location;
      } catch (error) {
        sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: "No response from API",
          reason: "no_response",
        });

        triggerErrorNotification({
          title: <Trans id="Something went wrong">Something went wrong</Trans>,
          message: (
            <Trans id="sign-up.api-error.something-went-wrong.text">
              We couldn’t complete your sign-up, please try again later.
            </Trans>
          ),
        });
      }
    },
    onError: async (error) => {
      const errorBody = await error.response.json();
      const errorCode = errorBody.error?.code;
      if (errorBody.error.code === HTTPStatusCode.InternalServerError) {
        triggerErrorNotification({
          title: <Trans id="Something went wrong">Something went wrong</Trans>,
          message: (
            <Trans id="sign-up.api-error.something-went-wrong.text">
              We couldn’t complete your sign-up, please try again later.
            </Trans>
          ),
        });
        sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: "We could not complete your sign-up, please try again later.",
          code: errorCode,
          reason: errorBody.error?.message,
        });
        return;
      }
      if (errorBody.error.code === HTTPStatusCode.RequestTimeout) {
        triggerErrorNotification({
          title: <Trans id="network-error.title">Network error</Trans>,
          message: (
            <Trans id="network-error.text">
              Please check your internet connection and try again.
            </Trans>
          ),
        });
        sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: "Please check your internet connection and try again.",
          code: errorCode,
          reason: errorBody.error?.message,
        });
        return;
      }
      const errors = await getErrorResponseErrors(errorBody);
      if (errors.length > 0) {
        const fieldErrors: FieldLevelErrorMap<SignUpFormField> = new Map();
        errors.forEach((err) => {
          if (err.field) {
            fieldErrors.set(
              err?.field as SignUpFormField,
              err as FieldLevelError
            );
          } else if (err.reason === SignUpAPIErrors.ReCAPTCHAInvalid) {
            triggerErrorNotification({
              message: (
                <Trans id="recaptcha-error">
                  There was a problem validating ReCAPTCHA. Please try again. If
                  this problem persists please contact support.
                </Trans>
              ),
            });
          } else {
            triggerErrorNotification({
              message: err.message,
            });
          }

          sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
            label: err?.message,
            code: errorCode,
            field: err?.field,
            reason: err?.message,
          });
        });
        setAPIFieldErrors(fieldErrors);
        setRegistrationBody({});
      }

      setIsSubmitting(false);
    },
  });

  useEffect(() => {
    if (registrationBody.email) {
      submitRegistration(registrationBody);
    }
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationBody]);

  const submitSignUpForm = (formData: SignUpFormData) => {
    setIsSubmitting(true);

    const {
      monthly_collections,
      annual_revenue,
      terms_and_conditions,
      ...remainingData
    } = formData;

    const requestBody: RegistrationCreateRequestBody = {
      signup_details: {
        monthly_collections: formData.monthly_collections || "",
        annual_revenue: formData.annual_revenue || "",
        terms_and_conditions: formData.terms_and_conditions,
      },
      pre_signup_device_id: preSignupDeviceID,
      ...remainingData,
    };

    setRegistrationBody(requestBody);
    setAPIFieldErrors(new Map());
  };

  return {
    apiFieldErrors,
    submitSignUpForm,
    isSubmitting,
  };
};
